import type { ApiSessionData } from '../api/types';
import type { DcId, SharedSessionData } from '../types';

import {
  DC_IDS,
  DEBUG, IS_SCREEN_LOCKED_CACHE_KEY,
  SESSION_ACCOUNT_PREFIX,
  SESSION_LEGACY_USER_KEY,
} from '../config';
import { getWindowLocalStorage } from './browserStorage';
import { DEBUG_PHONE_AUTH_MARKER } from './debugLoginMarkers';
import {
  ACCOUNT_SLOT,
  loadSlotSession as loadMultiaccountSlotSession,
  storeAccountData,
  writeSlotSession,
} from './multiaccount';

const DEBUG_PHONE_LOGIN_PAYLOAD_KEY = 'runai:debug-login:phone-payload';
const DEBUG_PHONE_LOGIN_RELOAD_KEY = 'runai:debug-login:phone-reload';

export function hasStoredSession() {
  if (checkSessionLocked()) {
    return true;
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (slotData) return Boolean(slotData.dcId);

  if (!ACCOUNT_SLOT) {
    const storage = getWindowLocalStorage();
    const legacyAuthJson = storage?.getItem(SESSION_LEGACY_USER_KEY);
    if (legacyAuthJson) {
      try {
        const userAuth = JSON.parse(legacyAuthJson);
        return Boolean(userAuth && userAuth.id && userAuth.dcID);
      } catch (err) {
        // Do nothing.
        return false;
      }
    }
  }

  return false;
}

export function storeSession(sessionData: ApiSessionData, currentUserId?: string) {
  const {
    mainDcId, keys, dcOptions, isTest,
  } = sessionData;

  const currentSlotData = loadSlotSession(ACCOUNT_SLOT);
  const newSlotData: SharedSessionData = {
    ...currentSlotData,
    dcId: mainDcId,
    dcOptions,
    isTest,
    ...(currentUserId ? { userId: currentUserId } : {}),
  };

  Object.keys(keys).map(Number).forEach((dcId) => {
    newSlotData[`dc${dcId as DcId}_auth_key`] = keys[dcId];
  });

  if (!ACCOUNT_SLOT) {
    storeLegacySession(sessionData, currentUserId ?? currentSlotData?.userId);
  }

  writeSlotSession(ACCOUNT_SLOT, newSlotData);
}

function storeLegacySession(sessionData: ApiSessionData, currentUserId?: string) {
  const {
    mainDcId, keys, dcOptions, isTest,
  } = sessionData;

  const storage = getWindowLocalStorage();
  storage?.setItem(SESSION_LEGACY_USER_KEY, JSON.stringify({
    dcID: mainDcId,
    dcOptions,
    id: currentUserId,
    test: isTest,
  }));
  storage?.setItem('dc', String(mainDcId));
  Object.keys(keys).map(Number).forEach((dcId) => {
    storage?.setItem(`dc${dcId}_auth_key`, JSON.stringify(keys[dcId]));
  });
}

export function clearStoredSession(slot?: number) {
  if (!slot) {
    clearStoredLegacySession();
  }

  const storage = getWindowLocalStorage();
  storage?.removeItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`);
}

function clearStoredSessionForDebugLogin(slot?: number) {
  clearStoredLegacySession();

  const storage = getWindowLocalStorage();
  storage?.removeItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`);
}

function clearStoredLegacySession() {
  const storage = getWindowLocalStorage();
  [
    SESSION_LEGACY_USER_KEY,
    'dc',
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    storage?.removeItem(key);
  });
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);

  if (!slotData) {
    if (ACCOUNT_SLOT) return undefined;
    return loadStoredLegacySession();
  }

  const sessionData: ApiSessionData = {
    mainDcId: slotData.dcId,
    keys: DC_IDS.reduce((acc, dcId) => {
      const key = slotData[`dc${dcId}_auth_key` as const];
      if (key) {
        acc[dcId] = key;
      }
      return acc;
    }, {} as Record<number, string>),
    ...(slotData.dcOptions ? { dcOptions: slotData.dcOptions } : {}),
    isTest: slotData.isTest || undefined,
  };

  return sessionData;
}

function loadStoredLegacySession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const storage = getWindowLocalStorage();
  const userAuth = JSON.parse(storage?.getItem(SESSION_LEGACY_USER_KEY) || 'null');
  if (!userAuth) {
    return undefined;
  }
  const mainDcId = Number(userAuth.dcID);
  const dcOptions = userAuth.dcOptions && typeof userAuth.dcOptions === 'object'
    ? userAuth.dcOptions as ApiSessionData['dcOptions']
    : undefined;
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};

  DC_IDS.forEach((dcId) => {
    try {
      const key = storage?.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load stored session', err);
      }
      // Do nothing.
    }
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId,
    keys,
    ...(dcOptions ? { dcOptions } : {}),
    isTest,
  };
}

export function loadSlotSession(slot: number | undefined): SharedSessionData | undefined {
  return loadMultiaccountSlotSession(slot);
}

export function updateSessionUserId(currentUserId: string) {
  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (!slotData) return;
  storeAccountData(ACCOUNT_SLOT, { userId: currentUserId });
}

export function importTestSession() {
  const sessionJson = process.env.TEST_SESSION!;
  try {
    const sessionData = JSON.parse(sessionJson) as ApiSessionData & { userId: string };
    storeLegacySession(sessionData, sessionData.userId);
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load test session', err);
    }
  }
}

export function checkSessionLocked() {
  const storage = getWindowLocalStorage();
  return storage?.getItem(IS_SCREEN_LOCKED_CACHE_KEY) === 'true';
}

// ---------------------------------------------------------------------------
// Debug Login: instant session injection via ?debug_login=<handle> URL parameter.
// Skips the entire login flow by injecting a pre-captured session.
// Works in all builds, protected by IP whitelist.
//
// Usage (always use handle, never numeric):
//   ?debug_login=mk       → MK 136907713 (pre-captured session)
//   ?debug_login=pd       → PD 136907715 (pre-captured session)
//   ?debug_login=escoo    → ES COO (auto phone login, dev only)
//   ?debug_login=phone:+16505212520 → dynamic phone login
// ---------------------------------------------------------------------------

// Pre-captured sessions are DEPRECATED — they break when the server rotates/deletes auth keys.
// All debug users should use phone auto-login (DEBUG_PHONE_USERS) instead.
const DEBUG_SESSIONS: Record<string, SharedSessionData> = {};

// Phone-based debug users — auto phone + code flow.
// Backend accepts magic code 12345 when client IP is in DebugLoginWhitelistIPs.
// This is the ONLY supported debug login method (pre-captured sessions are deprecated).
const DEBUG_PHONE_USERS: Record<string, { phone: string; code: string; password?: string }> = {
  mk: { phone: '+16502859925', code: '12345' },
  nb: { phone: '+16502859925', code: '12345' },
  pd: { phone: '+16504479661', code: '12345' },
  pl: { phone: '+16502859915', code: '12345' },
  escoo: { phone: '+8600100001', code: '12345' },
  testa: { phone: '+16505559901', code: '12345' },
  testb: { phone: '+16505559902', code: '12345' },
  zkf: { phone: '+8613640627270', code: '12345' },
  // Canonical shipping-account aliases used by sage_onboarding live lanes and UJM review.
  desktoptesta: { phone: '+16505550770', code: '12345' },
  sagefresha: { phone: '+16505550775', code: '12345' },
};

function injectSessionForUser(key: string): boolean {
  const session = DEBUG_SESSIONS[key];
  if (!session) return false;
  writeSlotSession(undefined, session);
  return true;
}

export function injectDebugSession(): boolean {
  return injectSessionForUser('mk');
}

// Clear only MTProto auth keys from localStorage (not user_auth or other metadata).
// Used by debug_login to force a fresh DH handshake when the stored key is stale.
// Safe: only affects THIS browser's localStorage; other devices keep their own keys.
export function clearStoredAuthKeysOnly() {
  DC_IDS.forEach((dcId) => {
    localStorage.removeItem(`dc${dcId}_auth_key`);
    localStorage.removeItem(`dc${dcId}_hash`);
    localStorage.removeItem(`dc${dcId}_server_salt`);
  });
  // Also clear the slot-based session which embeds auth keys
  const slotKey = `${SESSION_ACCOUNT_PREFIX}${ACCOUNT_SLOT || 1}`;
  const raw = localStorage.getItem(slotKey);
  if (raw) {
    try {
      const data = JSON.parse(raw);
      // Remove only auth key fields, keep userId and other metadata
      DC_IDS.forEach((dcId) => {
        delete data[`dc${dcId}_auth_key`];
      });
      localStorage.setItem(slotKey, JSON.stringify(data));
    } catch { /* corrupt slot data — remove it entirely */
      localStorage.removeItem(slotKey);
    }
  }
}

export function clearCurrentSlotAuthKeysOnly() {
  const slotKey = `${SESSION_ACCOUNT_PREFIX}${ACCOUNT_SLOT || 1}`;
  const raw = localStorage.getItem(slotKey);

  if (raw) {
    try {
      const data = JSON.parse(raw);
      DC_IDS.forEach((dcId) => {
        delete data[`dc${dcId}_auth_key`];
      });
      localStorage.setItem(slotKey, JSON.stringify(data));
    } catch {
      localStorage.removeItem(slotKey);
    }
  }

  if (!ACCOUNT_SLOT) {
    DC_IDS.forEach((dcId) => {
      localStorage.removeItem(`dc${dcId}_auth_key`);
      localStorage.removeItem(`dc${dcId}_hash`);
      localStorage.removeItem(`dc${dcId}_server_salt`);
    });
  }
}

// Store pending phone login config on window for Auth.tsx to pick up.
// Auth component has access to getActions() and drives the actual auth flow.
function schedulePhoneLogin(phoneUser: { phone: string; code: string; password?: string }) {
  (window as any).__pendingPhoneLogin = phoneUser;
  try {
    window.sessionStorage.setItem(DEBUG_PHONE_AUTH_MARKER, '1');
    window.sessionStorage.setItem(DEBUG_PHONE_LOGIN_PAYLOAD_KEY, JSON.stringify(phoneUser));
  } catch {
    // ignore storage failures
  }
  triggerPendingPhoneLogin();
}

function restoreScheduledPhoneLogin() {
  try {
    if ((window as any).__pendingPhoneLogin) {
      return;
    }

    const raw = window.sessionStorage.getItem(DEBUG_PHONE_LOGIN_PAYLOAD_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as { phone: string; code: string; password?: string };
    if (!parsed?.phone || !parsed?.code) {
      return;
    }

    (window as any).__pendingPhoneLogin = parsed;
  } catch {
    // ignore corrupt storage
  }
}

function reloadForDebugPhoneAuthBootstrap() {
  try {
    if (window.sessionStorage.getItem(DEBUG_PHONE_LOGIN_RELOAD_KEY) === '1') {
      return false;
    }

    window.sessionStorage.setItem(DEBUG_PHONE_LOGIN_RELOAD_KEY, '1');
    const url = new URL(window.location.href);
    url.searchParams.delete('debug_login');
    window.location.replace(`${url.pathname}${url.search}${url.hash}`);
    return true;
  } catch {
    return false;
  }
}

function triggerPendingPhoneLogin() {
  const state = window as any;
  if (state.__pendingPhoneLoginTriggerTimer) {
    clearTimeout(state.__pendingPhoneLoginTriggerTimer);
    state.__pendingPhoneLoginTriggerTimer = undefined;
  }

  const attempt = (remainingRetries: number) => {
    if (!state.__pendingPhoneLogin) {
      state.__pendingPhoneLoginTriggerTimer = undefined;
      return;
    }

    const authState = typeof state.__getGlobal === 'function'
      ? state.__getGlobal()?.auth?.state
      : undefined;
    const actions = typeof state.__getActions === 'function' ? state.__getActions() : undefined;
    const isWaitingForPhoneNumber = authState === 'authorizationStateWaitPhoneNumber';
    if (isWaitingForPhoneNumber) {
      if (typeof state.__callApi === 'function' || actions?.setAuthPhoneNumber) {
        const pendingLogin = state.__pendingPhoneLogin;
        delete state.__pendingPhoneLogin;
        state.__pendingAutoCode = {
          code: pendingLogin.code,
          password: pendingLogin.password,
          phone: pendingLogin.phone,
        };
        if (typeof state.__callApi === 'function') {
          state.__callApi('provideAuthPhoneNumber', pendingLogin.phone.replace(/[^\d]/g, ''));
        } else {
          actions.setAuthPhoneNumber({ phoneNumber: pendingLogin.phone });
        }
        state.__pendingPhoneLoginTriggerTimer = undefined;
        return;
      }
    } else if (authState && authState !== 'authorizationStateWaitQrCode') {
      state.__pendingPhoneLoginTriggerTimer = undefined;
      return;
    }

    if (!isWaitingForPhoneNumber && actions?.returnToAuthPhoneNumber) {
      actions.returnToAuthPhoneNumber();
    }

    if (remainingRetries <= 0) {
      state.__pendingPhoneLoginTriggerTimer = undefined;
      return;
    }

    state.__pendingPhoneLoginTriggerTimer = window.setTimeout(() => attempt(remainingRetries - 1), 250);
  };

  attempt(120);
}

function stripQueryParamsFromCurrentUrl(paramNames: string[]) {
  const url = new URL(window.location.href);
  paramNames.forEach((paramName) => {
    url.searchParams.delete(paramName);
  });
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', nextUrl);
}

// IP whitelist for debug login (must match backend DebugLoginWhitelistIPs in bff.yaml)
const DEBUG_LOGIN_WHITELIST_IPS = [
  '72.15.58.115',
  '103.88.185.161',
  '2607:f0c8:8000:10::7',
];

// Synchronous IP check via Nginx endpoint. Returns true if client IP is whitelisted.
// In dev builds (DEBUG=true), always returns true (no network call needed).
function isDebugIPAllowed(): boolean {
  if (DEBUG) return true;
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/.well-known/client-ip', false); // synchronous
    xhr.send();
    if (xhr.status === 200) {
      const ip = xhr.responseText.trim();
      return DEBUG_LOGIN_WHITELIST_IPS.includes(ip);
    }
  } catch { /* network error = not allowed */ }
  return false;
}

// Module-level: detect ?debug_login and handle accordingly.
// Works in all builds. Security: IP whitelist (frontend + backend defense-in-depth).
if (typeof window !== 'undefined') {
  restoreScheduledPhoneLogin();
  const debugParams = new URLSearchParams(window.location.search);
  const debugLoginValue = debugParams.get('debug_login');

  if (typeof debugLoginValue === 'string') {
    let shouldStripDebugLogin = true;
    if (isDebugIPAllowed()) {
      const preCapSession = DEBUG_SESSIONS[debugLoginValue];
      const phoneUser = DEBUG_PHONE_USERS[debugLoginValue];

      if (preCapSession) {
        // Pre-captured session (instant login: pd, david, etc.)
        injectSessionForUser(debugLoginValue);
      } else if (phoneUser) {
        // Phone-based auto-login — Auth.tsx picks this up.
        // Clear both the slot session and top-level MTProto auth keys so stale
        // browser-local crypto state cannot poison the fresh debug_login bootstrap.
        window.sessionStorage.removeItem('runai:debug-login:submitted-phone');
        clearStoredAuthKeysOnly();
        clearStoredSessionForDebugLogin(ACCOUNT_SLOT);
        schedulePhoneLogin(phoneUser);
        shouldStripDebugLogin = !reloadForDebugPhoneAuthBootstrap();
      } else if (debugLoginValue.startsWith('phone:')) {
        // Dynamic phone login: ?debug_login=phone:+16505212520
        // Uses magic code 12345 (backend accepts if IP whitelisted)
        window.sessionStorage.removeItem('runai:debug-login:submitted-phone');
        clearStoredAuthKeysOnly();
        clearStoredSessionForDebugLogin(ACCOUNT_SLOT);
        const phone = debugLoginValue.slice(6);
        schedulePhoneLogin({ phone, code: '12345' });
        shouldStripDebugLogin = !reloadForDebugPhoneAuthBootstrap();
      } else {
        // Unknown handle — log available options instead of silently injecting a default
        // eslint-disable-next-line no-console
        console.warn(
          `[debug_login] Unknown handle "${debugLoginValue}". Available: `
          + `${Object.keys(DEBUG_SESSIONS).join(', ')}, ${Object.keys(DEBUG_PHONE_USERS).join(', ')}, phone:<number>`,
        );
      }
    }
    // Show which account was injected (prevents confusion during testing)
    const injectedSession = DEBUG_SESSIONS[debugLoginValue];
    const injectedName = injectedSession?.firstName
      || DEBUG_PHONE_USERS[debugLoginValue]?.phone
      || (debugLoginValue.startsWith('phone:') ? debugLoginValue.slice(6) : undefined)
      || 'unknown (try: mk, pd)';
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log(`[debug_login] Logged in as: ${injectedName} (${debugLoginValue})`);
      document.title = `${document.title} [${injectedName}]`;
    }, 2000);

    if (shouldStripDebugLogin) {
      // Strip only debug_login, preserve other query params such as ?account=2.
      stripQueryParamsFromCurrentUrl(['debug_login']);
    }
  }

  // Check for ?debug_taskpanel parameter
  const debugTaskPanel = debugParams.get('debug_taskpanel');
  if (debugTaskPanel === '1') {
    (window as any).__DEBUG_TASK_PANEL = true;
    // eslint-disable-next-line no-console
    console.log('[debug_taskpanel] Task panel simulation mode enabled');
    // Strip only debug_taskpanel, preserve unrelated query params.
    stripQueryParamsFromCurrentUrl(['debug_taskpanel']);
  }

  const debugRunAiActivity = debugParams.get('debug_runai_activity');
  if (DEBUG && debugRunAiActivity === '1') {
    (window as any).__DEBUG_RUNAI_ACTIVITY = true;
    // eslint-disable-next-line no-console
    console.log('[debug_runai_activity] RunAI activity simulation mode enabled');
    stripQueryParamsFromCurrentUrl(['debug_runai_activity']);
  }

  if (DEBUG) {
    // Console helper to capture current session for hardcoding (dev only)
    // Usage: log in manually, then run __captureDebugSession() in browser console
    (window as any).__captureDebugSession = () => {
      const data = localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}1`);
      if (!data) {
        // eslint-disable-next-line no-console
        console.warn('No session found in account1 slot. Log in first.');
        return undefined;
      }
      // eslint-disable-next-line no-console
      console.log('Copy this JSON into DEBUG_SESSION_DATA in src/util/sessions.ts:');
      // eslint-disable-next-line no-console
      console.log(data);
      return JSON.parse(data);
    };
  }
}
